/**
 * Issue #133 — the proximity primitive: ONE distance function, shared.
 *
 * Before this module there was no distance calculation anywhere in the
 * codebase. The only coordinate comparison that existed —
 * `findDuplicateGrounds` (`api/src/db/repos/grounds.ts`) — is an
 * axis-aligned ±0.005° bounding box OR'd with a name `LIKE`, ending in
 * `rows.slice(0, 5)`: the first five rows the query happened to return, in
 * no order at all. It is correct as a "this might be a duplicate" warning
 * and it is deliberately left alone; nothing here is built on it.
 *
 * Why haversine rather than sorting by degree difference: a degree of
 * longitude is 111 km at the equator and 84 km in Wellington, so a
 * degree-sort quietly distorts by latitude. From Wellington, a ground 0.5°
 * east is 41.8 km away while one 0.4° north is 44.5 km — a degree-sort
 * ranks the FARTHER one first. `distance.test.js` pins exactly that pair.
 *
 * Everything here is pure arithmetic over values the caller already holds.
 * There is no I/O in this module — see `nearMe.js` for why that matters to
 * issue #134's privacy design, and `distance.test.js` for the assertion
 * that keeps it true.
 */

import { coordsOf, hasCoords } from "./mapFit";

/** IUGG mean Earth radius, km. */
export const EARTH_RADIUS_KM = 6371.0088;

const toRadians = (deg) => (deg * Math.PI) / 180;

/**
 * A coordinate, or null. The one coercion rule, shared by everything that
 * accepts a latitude or a longitude (`nearMe.js` imports it too).
 *
 * `Number(null)` is 0 and `Number("")` is 0, so a bare `Number.isFinite`
 * check accepts a MISSING coordinate as Null Island — and then ranks every
 * ground by its distance from the Gulf of Guinea, confidently and in the
 * wrong order. Range-checked as well: a value outside ±90/±180 is broken
 * input, not a place, and haversine will happily return a plausible number
 * for it.
 */
export function coordinate(value, limit) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < -limit || n > limit) return null;
  return n;
}

/**
 * Great-circle distance in km between two `{ lat, lng }` points.
 *
 * `Math.min(1, …)` guards the asin domain: for two identical points float
 * error can push the square root a hair above 1 and `asin` returns NaN, so
 * "the distance from a ground to itself" would be the one input that breaks
 * a distance function.
 */
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const lat1 = coordinate(a.lat, 90);
  const lng1 = coordinate(a.lng, 180);
  const lat2 = coordinate(b.lat, 90);
  const lng2 = coordinate(b.lng, 180);
  if ([lat1, lng1, lat2, lng2].some((v) => v === null)) return null;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Distance from an origin to a ground row, or `null` when the row has no
 * coordinates the map could place.
 *
 * Coordinate validity is decided by `mapFit.hasCoords` — the same predicate
 * the map uses to decide what it can pin — so a ground can never be ranked
 * into a "nearest" list that the map then refuses to draw. (It is also why
 * a genuine 0 is treated as a real coordinate here: the prime meridian and
 * the equator both run through inhabited places.)
 */
export function distanceToGroundKm(origin, ground) {
  if (!hasCoords(ground)) return null;
  const [lat, lng] = coordsOf(ground);
  return haversineKm(origin, { lat, lng });
}

/**
 * How a distance IN KILOMETRES is said out loud.
 *
 * Under a kilometre, metres rounded to 10 m — "400 m" is a walk, "0.4 km"
 * is arithmetic. Under 10 km, one decimal. Above that, whole kilometres:
 * the difference between 42 km and 42.3 km changes no decision anyone
 * makes with this list.
 *
 * ISSUE #436 ITEM 1 — WHY THE UNIT IS IN THE NAME. This and
 * `groundDuplicates.js`'s `formatMetres` were BOTH exported as
 * `formatDistance`, from the same directory, taking different units. Both
 * call sites were correct, but import autocomplete offered either name in
 * either file, and `formatDistance(1500)` rendered "1500 km" from one and
 * "1.5 km" from the other with no error anywhere — a silently wrong number
 * on screens whose whole job is to be trusted about distance. The unit is
 * now part of both names, and `distance.test.js` refuses a unitless one.
 */
export function formatKm(km) {
  if (km === null || km === undefined || !Number.isFinite(km)) return "";
  if (km < 1) {
    const metres = Math.round((km * 1000) / 10) * 10;
    // 995 m rounds to 1000 m, which should read as the kilometre it is.
    if (metres >= 1000) return "1.0 km";
    return `${metres} m`;
  }
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/**
 * Issue #133's `nearestGrounds(origin, grounds, { limit, maxKm })`.
 *
 * Returns the grounds within `maxKm` of `origin`, nearest first, each with
 * its distance attached as `_km` (and `_distanceLabel`, so a caller cannot
 * render the list without the number that makes it trustworthy — "Tōrvale
 * Club — 400 m", not an unexplained ordering).
 *
 * Out of range returns EMPTY, not nearest-anywhere: "nothing near you" is a
 * real answer with its own remedy (issue #134 offers to add the place), and
 * a list quietly headed by a ground 600 km away is worse than no list.
 *
 * Ties are broken by name so the ordering is stable — two grounds recorded
 * at the same coordinates (a real state, see `findDuplicateGrounds`) must
 * not swap places between renders.
 */
export function nearestGrounds(origin, grounds, { limit = 8, maxKm = 50 } = {}) {
  // The origin is guarded here, not only inside `haversineKm`, because the
  // failure mode differs: a bad origin must return NO answer, rather than an
  // answer ranked from somewhere the player has never been. `Number(null)`
  // is 0, so the obvious `Number.isFinite` check silently accepts
  // `{ lat: null, lng: null }` and ranks the world by its distance from
  // Null Island — see `coordinate`.
  const from = origin
    ? { lat: coordinate(origin.lat, 90), lng: coordinate(origin.lng, 180) }
    : null;
  if (!from || from.lat === null || from.lng === null) return [];
  const out = [];
  for (const g of grounds || []) {
    const km = distanceToGroundKm(from, g);
    if (km === null) continue;
    if (Number.isFinite(maxKm) && km > maxKm) continue;
    out.push({ ...g, _km: km, _distanceLabel: formatKm(km) });
  }
  out.sort((a, b) => (a._km - b._km) || String(a.name || "").localeCompare(String(b.name || "")));
  return Number.isFinite(limit) ? out.slice(0, limit) : out;
}
